<script setup>
import { computed } from 'vue';
import CardTypeIcon from './CardTypeIcon.vue';
import CardTaskStatusSelect from './CardTaskStatusSelect.vue';
import CardTaskDue from './CardTaskDue.vue';
import CardTaskAssigneePicker from './CardTaskAssigneePicker.vue';
import { OPEN_TASK_STATUSES } from '../cardStatus';
import { isScheduledVisit } from '../visitTask';

// One task's row: its type, title, assignee and status, each picker editable in place. A scheduled
// visit also shows when it happens; no other task shows a date, since a deadline next to the
// title read as when the task was created.
// Shared by the card page's task list and the task bubble on the conversation, so both work a
// task the same way. The pickers stop their click, so a row that toggles on click stays put.
// The pickers are one group: on a phone they take a line of their own under the title, aligned
// with it, so whatever follows the header in the row (the card page's chevron) stays on the
// title's line.
const props = defineProps({
  task: { type: Object, required: true },
  agents: { type: Array, required: true },
  // Draws the status as a call to action (see CardTaskRow).
  prominentStatus: { type: Boolean, default: false },
  // Offers to delete the task from its status menu (see CardTaskStatusSelect).
  deletable: { type: Boolean, default: false },
});

// `update` carries the attributes to write (assignee_id); `status` a transition.
const emit = defineEmits(['update', 'status', 'delete']);

const isOpen = computed(() => OPEN_TASK_STATUSES.includes(props.task.status));
</script>

<template>
  <CardTypeIcon :icon="task.type_icon" />
  <span
    class="flex-1 min-w-32 text-sm truncate"
    :class="
      isOpen
        ? 'text-n-slate-12'
        : 'text-n-slate-11 line-through decoration-n-slate-8'
    "
  >
    {{ task.title }}
  </span>
  <span
    class="flex items-center gap-3 ltr:ml-auto rtl:mr-auto max-sm:order-last max-sm:w-full max-sm:ltr:pl-7 max-sm:rtl:pr-7"
    data-testid="task-pickers"
  >
    <CardTaskDue v-if="isScheduledVisit(task) && task.due_at" :task="task" />
    <CardTaskAssigneePicker
      :task="task"
      :agents="agents"
      @click.stop
      @change="id => emit('update', { assignee_id: id })"
    />
    <CardTaskStatusSelect
      :task="task"
      :prominent="prominentStatus"
      :deletable="deletable"
      class="max-sm:ltr:ml-auto max-sm:rtl:mr-auto"
      @click.stop
      @change="transition => emit('status', transition)"
      @delete="emit('delete')"
    />
  </span>
</template>
